/**
 * The drawn sign-off (2026-10-06). Drawn once in your profile, stored as an
 * SVG path of pen strokes, and copied onto every letter as it is sealed — so
 * clearing it later leaves letters already sent signed, as paper would.
 *
 * Rendered as a stroke in `currentColor`, never as an image or markup: the
 * path is checked against `isSignaturePath` before it is drawn anywhere.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { AlertCircle, Eraser, PenLine, Undo2 } from 'lucide-react';
import {
  SIGNATURE_HEIGHT,
  SIGNATURE_WIDTH,
  isSignaturePath,
  signaturePath,
  type SignatureStroke,
} from '@toiyp/shared';
import { apiErrorMessage } from '../letters/ui';
import { useClearSignatureMutation, useSetSignatureMutation } from '../profiles/api';

const VIEWBOX = `0 0 ${SIGNATURE_WIDTH} ${SIGNATURE_HEIGHT}`;
const STROKE = 2.6;

/** A signature, drawn in the surrounding text colour. Nothing if the path is not ours. */
export function SignatureMark({ path, label, className = '' }: { path: string | null | undefined; label: string; className?: string }) {
  if (!isSignaturePath(path)) return null;
  return (
    <svg
      className={`signature-mark ${className}`.trim()}
      viewBox={VIEWBOX}
      role="img"
      aria-label={label}
      preserveAspectRatio="xMinYMid meet"
    >
      <path d={path} fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The foot of a letter: the author's sign-off, when they drew one. */
export function LetterSignature({ path, label }: { path: string | null | undefined; label: string }) {
  if (!isSignaturePath(path)) return null;
  return (
    <div className="letter-signature">
      <SignatureMark path={path} label={label} />
    </div>
  );
}

type Point = [number, number];

/**
 * A canvas you draw on with a finger, pen or mouse. Strokes are kept in
 * signature units (not pixels), so the saved path is the same on any screen.
 */
function SignaturePad({ onChange }: { onChange: (strokes: Point[][]) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  const [count, setCount] = useState(0);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const scale = canvas.width / SIGNATURE_WIDTH;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = STROKE * scale;
    ctx.strokeStyle = getComputedStyle(canvas).color;
    for (const s of strokes.current) {
      if (s.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(s[0]![0] * scale, s[0]![1] * scale);
      for (const [x, y] of s.length === 1 ? [[s[0]![0] + 0.1, s[0]![1]] as Point] : s.slice(1)) {
        ctx.lineTo(x * scale, y * scale);
      }
      ctx.stroke();
    }
  }, []);

  // Size the backing store to the element so lines stay crisp on any density.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const fit = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round((rect.width * SIGNATURE_HEIGHT * dpr) / SIGNATURE_WIDTH));
      redraw();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [redraw]);

  const toPoint = (e: PointerEvent<HTMLCanvasElement>): Point => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [
      ((e.clientX - rect.left) / rect.width) * SIGNATURE_WIDTH,
      ((e.clientY - rect.top) / rect.height) * SIGNATURE_HEIGHT,
    ];
  };

  const commit = () => {
    setCount(strokes.current.length);
    onChange(strokes.current);
  };

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        className="signature-pad__canvas"
        aria-label="Draw your signature here"
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          strokes.current = [...strokes.current, [toPoint(e)]];
          redraw();
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          strokes.current[strokes.current.length - 1]!.push(toPoint(e));
          redraw();
        }}
        onPointerUp={() => {
          if (!drawing.current) return;
          drawing.current = false;
          commit();
        }}
        onPointerCancel={() => {
          drawing.current = false;
          commit();
        }}
      />
      <span className="signature-pad__line" aria-hidden="true" />
      <div className="signature-pad__tools">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={count === 0}
          onClick={() => {
            strokes.current = strokes.current.slice(0, -1);
            redraw();
            commit();
          }}
        >
          <Undo2 size={13} aria-hidden="true" /> Undo stroke
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={count === 0}
          onClick={() => {
            strokes.current = [];
            redraw();
            commit();
          }}
        >
          <Eraser size={13} aria-hidden="true" /> Start over
        </button>
      </div>
    </div>
  );
}

/** Profile settings: draw, redraw or clear the sign-off. */
export function SignatureCard({ signature, handle }: { signature: string | null; handle: string | null }) {
  const [setSignature, { isLoading: saving }] = useSetSignatureMutation();
  const [clearSignature, { isLoading: clearing }] = useClearSignatureMutation();
  const [drawing, setDrawing] = useState(false);
  const [strokes, setStrokes] = useState<SignatureStroke[]>([]);
  const [error, setError] = useState<string | null>(null);
  const has = isSignaturePath(signature);
  const path = signaturePath(strokes);

  const onSave = async () => {
    setError(null);
    try {
      await setSignature({ path }).unwrap();
      setDrawing(false);
      setStrokes([]);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not save your signature'));
    }
  };

  const onClear = async () => {
    setError(null);
    try {
      await clearSignature().unwrap();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not clear your signature'));
    }
  };

  return (
    <section className="profile-fields signature-card" aria-label="Your signature">
      <h2 className="profile-fields__title">Your signature</h2>
      <p className="field__hint">
        Sign once and it goes at the foot of every letter you send from now on. Letters you
        have already sent keep the signature they went out with.
      </p>

      {drawing ? (
        <>
          <SignaturePad onChange={(s) => setStrokes(s.map((st) => [...st]))} />
          <div className="profile-actions signature-card__actions">
            <button type="button" className="btn btn--ghost" onClick={() => { setDrawing(false); setStrokes([]); }}>
              Cancel
            </button>
            <button type="button" className="btn btn--primary" disabled={saving || !path} onClick={() => void onSave()}>
              {saving ? 'Saving…' : 'Save signature'}
            </button>
          </div>
        </>
      ) : has ? (
        <>
          <div className="signature-card__preview">
            <SignatureMark path={signature} label={`Your signature${handle ? `, @${handle}` : ''}`} />
          </div>
          <div className="profile-actions signature-card__actions">
            <button type="button" className="btn btn--ghost" disabled={clearing} onClick={() => void onClear()}>
              {clearing ? 'Clearing…' : 'Clear'}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => setDrawing(true)}>
              <PenLine size={14} aria-hidden="true" /> Redraw
            </button>
          </div>
        </>
      ) : (
        <div className="profile-actions">
          <button type="button" className="btn btn--primary btn--block" onClick={() => setDrawing(true)}>
            <PenLine size={15} aria-hidden="true" /> Draw your signature
          </button>
        </div>
      )}

      {error && (
        <p className="form-error" role="alert">
          <AlertCircle size={13} aria-hidden="true" /> {error}
        </p>
      )}
    </section>
  );
}
